import * as React    from 'react';
import ReactPlayer   from 'react-player';

import { getRutubeId, getYoutubePlayableUrl, getYoutubeThumbnailUrl } from '../../../../../../common/tools/youtube';

import InternalVideo from '../../../../internal-video';

const GalleryImage = function(props) {
  const {
          alt = '',
          src,
          url,
          type,
          size,
        } = props;

  const isThumbnail = size <= 200;

  // Cross-origin iframes (YouTube/Rutube) swallow touch/mouse events inside their own browsing
  // context — Swiper never sees the touchmove, so swiping over a live video slide is impossible.
  // Rendering a static thumbnail + play button until the viewer explicitly clicks keeps the slide
  // swipeable like any image right up until that click; only Rutube needs its own facade state
  // here since react-player already ships one (its `light` prop) for the YouTube branch below.
  const [activated, setActivated] = React.useState(false);

  const ext = React.useMemo(() => {
    const [ext = ''] = /\.(\d|\w)+$/g.exec(src) || [];

    return ext;
  }, [src]);

  /**
   @param {number} size
   @param {number} [max]
   @param {number} [min]
   *
   * @returns {string}
   */
  const getSize   = React.useCallback((size, max, min) => {
    let result = '';

    if (min) {
      result += `(min-width: ${min}) `;
    } else if (max) {
      result += `(max-width: ${max}) `;
    }

    return `${result}${size}px`;
  }, []);
  /**
   * @param {number} size
   * @param {number} [max]
   * @param {number} [min]
   *
   * @returns {JSX.Element}
   */
  const getSource = React.useCallback((size, max, min) => {
    let media;
    if (typeof min !== 'undefined') {
      media = `(min-width: ${min}px)`;
    }

    if (max) {
      media = `${!!media ? `${media} and ` : ''}(max-width: ${max}px)`;
    }

    return (
      <>
        <source
          src={getSrc(size, 'webp')}
          media={media}
          type={'image/webp'}
        />
        <source
          src={getSrc(size)}
          media={media}
          type={'image/png'}
        />
      </>
    );
  }, []);
  /**
   * @param {number} size
   * @param {string} [ext='png']
   *
   * @returns {string}
   */
  const getSrc    = React.useCallback((size, ext = 'png') => {
    if (ext !== '.svg' && ext !== '.gif') {
      return `${src}.${ext}?w=${size}&default=horizontal&t=${BUILD_TIME}`;
    }

    return src;
  }, [ext, src, size]);
  /**
   * @param {number} size
   * @param {number} [breakpoint=size]
   *
   * @returns {string}
   */
  const getSrcSet = React.useCallback((size, breakpoint = size) => {
    return `${getSrc(size)} ${breakpoint}w`;
  }, [getSrc]);

  const sizes   = React.useMemo(() => {
    const items = [];

    if (ext !== '.svg' && ext !== '.gif') {
      if (size > 100) {
        items.push(getSize(320, 767));

        if (size >= 1152) {
          items.push(getSize(992, void 0, 768), getSize(1152, void 0, 992));

          if (size >= 1992) {
            items.push(getSize(1140, void 0, 1440), getSize(1920));
          }
        }
      } else {
        items.push(getSize(320));
      }
    }

    return items.join(', ');
  }, [ext, getSize, size]);
  const sources = React.useMemo(() => {
    const items = [];

    if (ext !== '.svg' && ext !== '.gif') {
      if (size > 100) {
        items.push(getSource(768, 768));

        if (size >= 1152) {
          items.push(getSource(992, 991, 768));
          items.push(getSource(1152, 1151, 992));

          if (size >= 1992) {
            items.push(getSource(1440, 1439, 1152));
            items.push(getSource(1920, void 0, 1440));
          }
        }
      } else {
        items.push(getSource(100));
      }
    }

    return items;
  }, [ext, getSource, size]);
  const srcSet  = React.useMemo(() => {
    const items = [];

    if (ext !== '.svg' && ext !== '.gif') {
      if (size > 100) {
        items.push(getSrcSet(768, 320));

        if (size >= 1152) {
          items.push(getSrcSet(992), getSrcSet(1152));

          if (size >= 1992) {
            items.push(getSrcSet(1440), getSrcSet(1920));
          }
        }
      } else {
        items.push(getSrcSet(100, 320));
      }
    }

    return items.join(' ');
  }, [ext, getSrcSet, size]);

  if (type === 'youtube') {
    if (!url) {
      return null;
    }

    // The stored marker covers both YouTube and Rutube links added via the same admin field —
    // react-player has no Rutube provider at all (falls back to a raw <video src>, which fails with
    // a native "unsupported format" error), so Rutube is rendered as its own official embed iframe
    // instead of going through ReactPlayer.
    const rutubeId = getRutubeId(url);

    if (rutubeId) {
      const thumbnailUrl = `https://rutube.ru/api/video/${rutubeId}/thumbnail/?redirect=1`;

      if (isThumbnail) {
        return (
          <div
            className={'gallery-video'}
            style={{ pointerEvents: 'none' }}
          >
            <img
              alt={alt}
              src={thumbnailUrl}
            />
          </div>
        );
      }

      if (!activated) {
        return (
          <div
            className={'gallery-video gallery-video--facade'}
            onClick={() => setActivated(true)}
          >
            <img
              alt={alt}
              src={thumbnailUrl}
            />
            <span className={'gallery-video__play'} />
          </div>
        );
      }

      return (
        <div className={'gallery-video'}>
          <iframe
            allow={'clipboard-write; autoplay'}
            allowFullScreen
            src={`https://rutube.ru/play/embed/${rutubeId}?autoplay=true`}
            title={alt || 'Rutube video'}
          />
        </div>
      );
    }

    return (
      <div
        className={'gallery-video'}
        style={isThumbnail ? { pointerEvents: 'none' } : undefined}
      >
        <ReactPlayer
          config={{
            youtube: {
              playerVars: {
                autoplay: 1,
                controls: 1,
                loop:     0,
                showinfo: 0,
              },
            },
          }}
          controls={!isThumbnail}
          height={'100%'}
          light={getYoutubeThumbnailUrl(url) || true}
          url={getYoutubePlayableUrl(url)}
          width={'100%'}
        />
      </div>
    );
  }

  if (type?.startsWith('video')) {
    if (!src) {
      return null;
    }

    return (
      <div
        className={'gallery-video'}
        style={isThumbnail ? { pointerEvents: 'none' } : undefined}
      >
        <InternalVideo
          controls={!isThumbnail}
          src={src}
          thumbnail={isThumbnail}
        />
      </div>
    );
  }

  if (!src) {
    return null;
  }

  return (
    <picture>
      {sources.map((el, i) => {
        return <React.Fragment key={i}>{el}</React.Fragment>;
      })}
      <img
        alt={alt}
        sizes={sizes}
        src={getSrc(1920, 'png')}
        srcSet={srcSet}
      />
    </picture>
  );
};

export default GalleryImage;